import { Injectable, TemplateRef } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { LazyLoadEvent, SelectItem } from 'primeng/api';
import { ColumnOutputType } from '../../component-library/table/column-output-type';
import { TableColumn } from '../../component-library/table/table-column';
import { TableConfig } from '../../component-library/table/table-config';
import { TableItemState } from '../../component-library/table/table-item-state.enum';
import { TableConfigHelperService } from '../../shared/table-helpers/table-config-helper-service';
import { RowsPerPage, defaultRowsPerPageOptions } from '../../shared/table-helpers/table-helper';
import { UomService } from '../../shared/uom/uom.service';
import { TemplateAttributeDataType } from '../../viewmodels/admin/hierarchy-templates/template-attribute-data-type.enum';
import { TableTemplateColumn } from '../../viewmodels/admin/table-attribute-template/table-template-column';
import { Unit } from '../../viewmodels/unit';

@Injectable({
  providedIn: 'root',
})
export class TableAttributeTemplateTableConfigService {
  public static readonly rowIndexColumnName = 'rowIndex';
  public tableConfig: TableConfig = new TableConfig();
  public lookupItems = [
    {
      label: this.i18NextPipe.transform('TableAttributeTemplate.AttributesTable.SelectType'),
      value: null,
    },
    {
      label: this.i18NextPipe.transform('TableAttributeTemplate.AttributesTable.' + TemplateAttributeDataType[TemplateAttributeDataType.Number]),
      value: TemplateAttributeDataType.Number,
    },
    {
      label: this.i18NextPipe.transform('TableAttributeTemplate.AttributesTable.' + TemplateAttributeDataType[TemplateAttributeDataType.Text]),
      value: TemplateAttributeDataType.Text,
    },
    {
      label: this.i18NextPipe.transform('TableAttributeTemplate.AttributesTable.' + TemplateAttributeDataType[TemplateAttributeDataType.DateTime]),
      value: TemplateAttributeDataType.DateTime,
    },
    {
      label: this.i18NextPipe.transform('TableAttributeTemplate.AttributesTable.' + TemplateAttributeDataType[TemplateAttributeDataType.Boolean]),
      value: TemplateAttributeDataType.Boolean,
    },
  ];

  public uomOptions: SelectItem[] = [];
  public uomList: Unit[];

  public constructor(private i18NextPipe: I18NextPipe, private uomService: UomService, private helperService: TableConfigHelperService) {
    this.loadUOM();
  }

  public initColumnTableConfig(
    typeTemplateRef: TemplateRef<any>,
    uomTemplateRef: TemplateRef<any>,
    nameTemplateRef: TemplateRef<any>,
    canModify: boolean = false,
  ): void {
    const config = this.createBaseTableConfig();

    config.id = 'table-id';
    config.title = this.i18NextPipe.transform('TableAttributeTemplate.AttributesTable.Title');
    config.deleteActionTitle = this.i18NextPipe.transform('TableAttributeTemplate.AttributesTable.Delete');

    config.columns = this.createColumns(typeTemplateRef, uomTemplateRef, nameTemplateRef);

    config.lazyLoading = true;
    config.lazyLoadOnInit = false;

    config.rowsPerPage = RowsPerPage.Large;
    config.rowsPerPageOptions = defaultRowsPerPageOptions;
    config.totalRecords = 0;

    config.loading = false;

    this.setConfigOperationState(canModify, config);
    config.rowHighlightFunction = rowData => this.setRowStyleClass(rowData);
    this.tableConfig = config;
  }

  private setRowStyleClass(rowData: TableTemplateColumn): string {
    return rowData.state === TableItemState.Added || rowData.state === TableItemState.Modified ? 'row-default-warning' : null;
  }

  private createColumns(typeTemplateRef: TemplateRef<any>, uomTemplateRef: TemplateRef<any>, nameTemplateRef: TemplateRef<any>): TableColumn[] {
    const nameColumn = new TableColumn(this.i18NextPipe.transform('TableAttributeTemplate.Name'), 'name');

    nameColumn.outputType = ColumnOutputType.Custom;
    nameColumn.customOutputTemplateRef = nameTemplateRef;
    nameColumn.modelChangeEvent = (row: TableTemplateColumn): void => this.onRowChangeEvent(row);

    const typeColumn = new TableColumn(this.i18NextPipe.transform('TableAttributeTemplate.Type'), 'dataType');

    typeColumn.outputType = ColumnOutputType.Custom;
    typeColumn.customOutputTemplateRef = typeTemplateRef;
    typeColumn.modelChangeEvent = (row: TableTemplateColumn): void => this.onRowChangeEvent(row);

    const uOMColumn = new TableColumn(this.i18NextPipe.transform('TableAttributeTemplate.Uom'), 'uom');

    uOMColumn.outputType = ColumnOutputType.Custom;
    uOMColumn.customOutputTemplateRef = uomTemplateRef;
    uOMColumn.modelChangeEvent = (row: TableTemplateColumn): void => this.onRowChangeEvent(row);

    return [nameColumn, typeColumn, uOMColumn];
  }

  private createBaseTableConfig(): TableConfig {
    const config = new TableConfig();

    config.enableDelete = false;
    config.enableFiltering = false;
    config.enablePagination = true;
    config.enableSorting = false;
    config.showRowHighlightInfoColumn = false;

    return config;
  }

  public setConfigOperationState(canModify: boolean, config: TableConfig = this.tableConfig): void {
    config.enableAdd = canModify;
    config.enableEdit = canModify;
    config.enableSelectionColumn = canModify;
    this.setDeleteTableActions(config, canModify);
  }

  private setDeleteTableActions(config: TableConfig, canModify: boolean): void {
    config.actions = [this.helperService.createDeleteTableAction(config.deleteActionTitle, canModify)];
  }

  private loadUOM(): void {
    this.uomService.load().subscribe(result => {
      if (result) {
        this.uomOptions.push({ label: this.i18NextPipe.transform('TableAttributeTemplate.AttributesTable.None'), value: null });
        this.uomList = this.uomService.getUoms();
        this.uomList.forEach(uom => {
          this.uomOptions.push({ value: uom.uomId, label: uom.uomName });
        });
      }
    });
  }

  public onRowChangeEvent(row: TableTemplateColumn): void {
    if (row.state !== TableItemState.Added) {
      row.state = TableItemState.Modified;
    }
  }

  public get defaultAttributeLoadEvent(): LazyLoadEvent {
    return {
      filters: {},
      first: 0,
      rows: RowsPerPage.Large,
      sortOrder: 1,
    };
  }

  public setTablePaginationConfig(recordsCount: number): void {
    this.tableConfig.first = 0;
    this.tableConfig.totalRecords = recordsCount;
    this.tableConfig.rowsPerPage = RowsPerPage.Large;
  }
}
